import React, { useState, useEffect } from 'react';
import { RefreshCw, Users, X, MessageSquare, FileText, MessageCircle, TrendingUp } from 'lucide-react';
import API_URL from '../config/ApiUrl';
import { useTheme } from '../arrangement/ThemeContext';
export function UsersModal({ isOpen, onClose }) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userError, setUserError] = useState('');
  const { colors } = useTheme();
  

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    setUserError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      setUserError(err.message);
      console.error('User fetch error:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const formatLastSeen = (dateString) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '24px'
    }}>
      <div style={{
        background: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '1200px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Dialog Header */}
        <div style={{
          padding: '24px',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Users size={24} style={{ color: '#3b82f6' }} />
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#e5e5e5' }}>
              User Management
            </h2>
            {!loadingUsers && (
              <span style={{
                background: '#3b82f620',
                color: '#3b82f6',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {users.length} users
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Dialog Content */}
        <div style={{
          padding: '24px',
          overflowY: 'auto',
          flex: 1
        }}>
          {userError && (
            <div style={{
              background: '#ef444420',
              border: '1px solid #ef4444',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
              color: '#ef4444',
              fontSize: '14px'
            }}>
              Error: {userError}
            </div>
          )}

          {loadingUsers ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px',
              color: '#888'
            }}>
              Loading users...
            </div>
          ) : users.length === 0 ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              padding: '48px',
              color: '#888'
            }}>
              <Users size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p>No users found</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {users.map(user => (
                <div
                  key={user.id}
                  style={{
                    background: colors.bgCard,
                    border: '1px solid #333',
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}
                >
                  {/* User Header */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto',
                    gap: '16px',
                    alignItems: 'center'
                  }}>
                    {/* Profile Image */}
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: user.profile_image_url 
                        ? `url(${user.profile_image_url}) center/cover` 
                        : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '18px'
                    }}>
                      {!user.profile_image_url && user.username.charAt(0).toUpperCase()}
                    </div>

                    {/* User Info */}
                    <div>
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#e5e5e5',
                        marginBottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flexWrap: 'wrap'
                      }}>
                        {user.username}
                        {user.last_seen && (
                          <span style={{
                            padding: '2px 8px',
                            background: '#3b82f620',
                            color: '#3b82f6',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            {formatLastSeen(user.last_seen)}
                          </span>
                        )}
                        {!user.is_active && (
                          <span style={{
                            padding: '2px 8px',
                            background: '#ef444420',
                            color: '#ef4444',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            Inactive
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', color: '#888', marginBottom: '4px' }}>
                        {user.email}
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        gap: '12px', 
                        fontSize: '12px', 
                        color: '#666',
                        flexWrap: 'wrap'
                      }}>
                        <span>ID: {user.id}</span>
                        <span>•</span>
                        <span>Level: {user.smoker_level}</span>
                        <span>•</span>
                        <span>Joined: {new Date(user.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <span style={{
                        padding: '6px 12px',
                        background: user.is_active ? '#10b98120' : '#71717120',
                        color: user.is_active ? '#10b981' : '#717171',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                      }}>
                        {user.is_active ? '● Active' : '○ Inactive'}
                      </span>
                    </div>
                  </div>

                  {/* Activity Stats */}
                  {user.activity && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: '12px',
                      paddingTop: '12px',
                      borderTop: '1px solid #333'
                    }}>
                      {/* Threads */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        background: '#1a1a1a',
                        borderRadius: '8px',
                        border: '1px solid #2a2a2a'
                      }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: '#6366f120',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <MessageSquare size={16} style={{ color: '#6366f1' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: '#e5e5e5' }}>
                            {user.activity.threads}
                          </div>
                          <div style={{ fontSize: '11px', color: '#888' }}>
                            Threads
                          </div>
                        </div>
                      </div>

                      {/* Thread Messages */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        background: '#1a1a1a',
                        borderRadius: '8px',
                        border: '1px solid #2a2a2a'
                      }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: '#3b82f620',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <MessageCircle size={16} style={{ color: '#3b82f6' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: '#e5e5e5' }}>
                            {user.activity.thread_messages}
                          </div>
                          <div style={{ fontSize: '11px', color: '#888' }}>
                            Messages
                          </div>
                        </div>
                      </div>

                      {/* Reviews */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        background: '#1a1a1a',
                        borderRadius: '8px',
                        border: '1px solid #2a2a2a'
                      }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: '#f59e0b20',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <FileText size={16} style={{ color: '#f59e0b' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: '#e5e5e5' }}>
                            {user.activity.reviews}
                          </div>
                          <div style={{ fontSize: '11px', color: '#888' }}>
                            Reviews
                          </div>
                        </div>
                      </div>

                      {/* Review Replies */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        background: '#1a1a1a',
                        borderRadius: '8px',
                        border: '1px solid #2a2a2a'
                      }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: '#10b98120',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <MessageCircle size={16} style={{ color: '#10b981' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: '#e5e5e5' }}>
                            {user.activity.review_replies}
                          </div>
                          <div style={{ fontSize: '11px', color: '#888' }}>
                            Replies
                          </div>
                        </div>
                      </div>

                      {/* Total Contributions */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        background: '#1a1a1a',
                        borderRadius: '8px',
                        border: '1px solid #8b5cf620'
                      }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: '#8b5cf620',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <TrendingUp size={16} style={{ color: '#8b5cf6' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: '#8b5cf6' }}>
                            {user.activity.total_contributions}
                          </div>
                          <div style={{ fontSize: '11px', color: '#888' }}>
                            Total
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Dialog Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #333',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={fetchUsers}
            disabled={loadingUsers}
            style={{
              padding: '10px 20px',
              background: colors.bgCard,
              color: '#e5e5e5',
              border: '1px solid #333',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loadingUsers ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: loadingUsers ? 0.5 : 1
            }}
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}